/**
 * Decimal arithmetic on money, without floats.
 *
 * Every amount in this system is a decimal string, because the driver hands
 * them over as text and a float cannot hold 0.1 (D-018). That rule held right
 * up to the quote grid, where a footer has to update as somebody types - and
 * the first version did it with `Number` and `toFixed`, which the lint rule
 * caught. It was right to: 1234.56 * 3 is 3703.6800000000003 in a double, and
 * a grid whose lines do not add up to its footer is a grid nobody trusts.
 *
 * So the arithmetic happens on scaled integers. Values are parsed into
 * `bigint` at four decimal places - enough to multiply a quantity by a price
 * and take a percentage off without losing anything - and rounded to two only
 * when a string comes back out.
 *
 * This is deliberately the same arithmetic the database's generated
 * `line_total` column performs. A test asserts they agree, because two
 * implementations of "quantity times price less discount" are exactly the sort
 * of pair that drifts (P-1).
 */

/** Working precision. Four places holds a percentage of a price exactly. */
const SCALE = 4n;
const SCALE_FACTOR = 10n ** SCALE;

const DECIMAL = /^-?\d+(\.\d+)?$/;

/**
 * A decimal string as a scaled integer.
 *
 * Throws on anything that is not a decimal, rather than quietly reading it as
 * zero: a blank price that silently becomes free is the kind of defect that
 * reaches a customer's inbox.
 */
export function parseDecimal(value: string, what = 'value'): bigint {
  const trimmed = value.trim();
  if (trimmed === '') return 0n;
  if (!DECIMAL.test(trimmed)) {
    throw new Error(`${what} must be a decimal like 1500.00, not ${JSON.stringify(value)}.`);
  }
  const negative = trimmed.startsWith('-');
  const [whole = '0', fraction = ''] = trimmed.replace('-', '').split('.');
  const padded = (fraction + '0'.repeat(Number(SCALE))).slice(0, Number(SCALE));
  const scaled = BigInt(whole) * SCALE_FACTOR + BigInt(padded || '0');
  return negative ? -scaled : scaled;
}

/** A scaled integer back to a decimal string, half-up at `places`. */
export function toDecimalString(scaled: bigint, places = 2): string {
  const drop = SCALE - BigInt(places);
  let rounded = scaled;
  if (drop > 0n) {
    const divisor = 10n ** drop;
    const negative = scaled < 0n;
    const absolute = negative ? -scaled : scaled;
    // Half-up, matching Postgres's `round()` on numeric.
    const quotient = (absolute + divisor / 2n) / divisor;
    rounded = negative ? -quotient : quotient;
  } else if (drop < 0n) {
    // More places than the scale holds: pad, don't reinterpret. Without this,
    // six places read a four-place value as if it had six, and 72 came out as
    // 0.72.
    rounded = scaled * 10n ** -drop;
  }
  const factor = 10n ** BigInt(places);
  const negative = rounded < 0n;
  const absolute = negative ? -rounded : rounded;
  const whole = absolute / factor;
  const fraction = absolute % factor;
  const text =
    places === 0 ? whole.toString() : `${whole.toString()}.${fraction.toString().padStart(places, '0')}`;
  return negative ? `-${text}` : text;
}

/** Multiplies two scaled integers, keeping the scale. */
function multiplyScaled(a: bigint, b: bigint): bigint {
  return (a * b) / SCALE_FACTOR;
}

export interface LineAmounts {
  quantity: string;
  unitPrice: string;
  discountPercent: string;
}

/**
 * Quantity x price, less a percentage. Two decimal places.
 *
 * The same expression as `quote_line.line_total`, which is a generated column
 * in the database. Keeping them in step is what `money.test.ts` is for.
 */
export function lineTotal(line: LineAmounts): string {
  const quantity = parseDecimal(line.quantity, 'A quantity');
  const price = parseDecimal(line.unitPrice, 'A price');
  const discount = parseDecimal(line.discountPercent || '0', 'A discount');
  const gross = multiplyScaled(quantity, price);
  const kept = SCALE_FACTOR - discount / 100n;
  return toDecimalString(multiplyScaled(gross, kept), 2);
}

export function sumDecimals(values: readonly string[]): string {
  let total = 0n;
  for (const value of values) total += parseDecimal(value);
  return toDecimalString(total, 2);
}

export function percentOf(amount: string, percent: string): string {
  const scaled = parseDecimal(amount, 'An amount');
  const rate = parseDecimal(percent || '0', 'A percentage');
  return toDecimalString(multiplyScaled(scaled, rate / 100n), 2);
}

/**
 * Quantity times price, as a decimal string.
 *
 * Here rather than in the caller, for the reason the rest of this file exists:
 * money arithmetic done in floating point is wrong by a cent on the invoice
 * somebody queries, and a second implementation is a second chance to round
 * differently from `lineTotal`.
 */
export function multiplyDecimals(a: string, b: string, places = 2): string {
  return toDecimalString(multiplyScaled(parseDecimal(a, 'An amount'), parseDecimal(b, 'An amount')), places);
}

/**
 * One amount as a proportion of another.
 *
 * Returns '0' when the divisor is zero rather than throwing or producing
 * Infinity: the caller decides whether a zero divisor is meaningful, and for a
 * margin it is not - a line given away free has no margin percentage, and
 * saying so is the caller's job rather than this function's.
 */
export function divideDecimals(a: string, b: string, places = 4): string {
  const divisor = parseDecimal(b, 'An amount');
  if (divisor === 0n) return '0';
  const scaled = (parseDecimal(a, 'An amount') * SCALE_FACTOR) / divisor;
  return toDecimalString(scaled, places);
}

export function addDecimals(a: string, b: string): string {
  return toDecimalString(parseDecimal(a) + parseDecimal(b), 2);
}

export function subtractDecimals(a: string, b: string): string {
  return toDecimalString(parseDecimal(a) - parseDecimal(b), 2);
}

export interface QuoteLineAmounts extends LineAmounts {
  optional: boolean;
}

export interface ComputedTotals {
  subtotal: string;
  /** Stated separately: an option nobody has chosen is not revenue. */
  optionalTotal: string;
  discountAmount: string;
  total: string;
}

export function computeQuoteTotals(
  lines: readonly QuoteLineAmounts[],
  discountPercent: string,
): ComputedTotals {
  const subtotal = sumDecimals(lines.filter((line) => !line.optional).map(lineTotal));
  const optionalTotal = sumDecimals(lines.filter((line) => line.optional).map(lineTotal));
  const discountAmount = percentOf(subtotal, discountPercent);
  return {
    subtotal,
    optionalTotal,
    discountAmount,
    total: subtractDecimals(subtotal, discountAmount),
  };
}

/**
 * Tax, computed per line and then summed (G-113).
 *
 * **Per line, not on the total.** Rounding once at the bottom gives a
 * different answer from rounding each line, and the second is what every
 * accounting system and every tax authority expects. On a twenty-line quote
 * the two differ by a few fils, which is enough for an invoice to be rejected
 * and for somebody to spend an afternoon on it.
 *
 * **Inclusive pricing is not a display option.** 100 at 5% inclusive is 95.24
 * of goods and 4.76 of tax; exclusive it is 100 and 5. The same document says
 * two different things depending on which was meant, so the flag travels with
 * the arithmetic rather than sitting in a formatter.
 */
export interface TaxedLine extends QuoteLineAmounts {
  /** The rate for this line, as a percentage. A line may differ from the quote. */
  taxPercent: string;
}

export interface TaxBreakdown {
  /** The rate, so a document can show "VAT at 5%" per band. */
  percent: string;
  /** What was taxed at it, after discount. */
  net: string;
  tax: string;
}

export interface TaxedTotals extends ComputedTotals {
  /** Goods and services, after discount, before tax. */
  net: string;
  tax: string;
  /** Net plus tax. What the customer pays. */
  gross: string;
  /** One row per distinct rate, because a document with mixed rates must show them. */
  bands: TaxBreakdown[];
}

/**
 * The tax inside an amount that already contains it.
 *
 * `amount x percent / (100 + percent)`, in scaled integers throughout: a
 * float here would put a fraction of a fils into every inclusive line, and
 * twenty of those is a document that does not add up.
 */
export function taxWithin(amount: string, percent: string): string {
  const rate = parseDecimal(percent, 'percent');
  if (rate === 0n) return '0.00';
  const scaledAmount = parseDecimal(amount);
  // Both sides carry SCALE_FACTOR, so the hundred does too.
  const denominator = 100n * SCALE_FACTOR + rate;
  const halfUp = (scaledAmount * rate + denominator / 2n) / denominator;
  return toDecimalString(halfUp, 2);
}

export function computeTaxedTotals(
  lines: readonly TaxedLine[],
  discountPercent: string,
  taxInclusive: boolean,
): TaxedTotals {
  const base = computeQuoteTotals(lines, discountPercent);

  /*
   * The discount is spread across the lines before tax is taken.
   *
   * A discount applied to the total and tax taken from the undiscounted lines
   * would charge tax on money nobody paid. Each line is reduced by the same
   * percentage, which is what a percentage discount means.
   */
  const bands = new Map<string, { net: bigint; tax: bigint }>();
  for (const line of lines) {
    if (line.optional) continue;
    const gross = lineTotal(line);
    const afterDiscount = subtractDecimals(gross, percentOf(gross, discountPercent));

    const net = taxInclusive
      ? subtractDecimals(afterDiscount, taxWithin(afterDiscount, line.taxPercent))
      : afterDiscount;
    const tax = taxInclusive
      ? taxWithin(afterDiscount, line.taxPercent)
      : percentOf(afterDiscount, line.taxPercent);

    const band = bands.get(line.taxPercent) ?? { net: 0n, tax: 0n };
    bands.set(line.taxPercent, {
      net: band.net + parseDecimal(net),
      tax: band.tax + parseDecimal(tax),
    });
  }

  let net = 0n;
  let tax = 0n;
  for (const band of bands.values()) {
    net += band.net;
    tax += band.tax;
  }

  return {
    ...base,
    net: toDecimalString(net, 2),
    tax: toDecimalString(tax, 2),
    gross: toDecimalString(net + tax, 2),
    bands: [...bands.entries()]
      // Highest rate first: the standard band is the one a reader checks.
      .sort((a, b) => parseDecimal(b[0], 'percent') > parseDecimal(a[0], 'percent') ? 1 : -1)
      .map(([percent, amounts]) => ({
        percent,
        net: toDecimalString(amounts.net, 2),
        tax: toDecimalString(amounts.tax, 2),
      })),
  };
}
